type CountdownData = {
    closed: boolean;
    text: string;
    urgent: boolean;
};

type SecondsMode = 'never' | 'under-hour';

let countdownInterval: ReturnType<typeof setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;

function formatMeetingCountdownText(secs: number): string {
    const d = Math.floor(secs / 86400);
    const h = String(Math.floor((secs % 86400) / 3600)).padStart(2, '0');
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    const clock = `${h}:${m}:${s}`;
    if (d > 0) {
        return `${d} ${d === 1 ? 'day' : 'days'}, ${clock}`;
    }
    return clock;
}

function formatCountdownText(secs: number, showSeconds: boolean): string {
    if (secs >= 86400) {
        const d = Math.floor(secs / 86400);
        const h = Math.floor((secs % 86400) / 3600);
        return `${d}d ${h}h`;
    }
    if (secs >= 3600) {
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        return `${h}h ${m}m`;
    }
    if (showSeconds) {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}m ${String(s).padStart(2, '0')}s`;
    }
    const m = Math.max(1, Math.ceil(secs / 60));
    return `${m}m`;
}

function formatFooterCountdown(endMs: number, nowMs: number, secondsMode: SecondsMode): CountdownData {
    const secs = Math.floor((endMs - nowMs) / 1000);
    if (secs <= 0) {
        return { closed: true, text: 'Closed', urgent: false };
    }
    const showSeconds = secondsMode === 'under-hour' && secs < 3600;
    return {
        closed: false,
        text: formatCountdownText(secs, showSeconds),
        urgent: secs < 3600,
    };
}

function syncCountdownGroup(el: HTMLElement, closed: boolean, urgent: boolean): void {
    const group = el.closest('.footer-mega__info-group--countdown');
    group?.classList.toggle('footer-mega__info-group--closed', closed);
    group?.classList.toggle('footer-mega__info-group--urgent', !closed && urgent);
}

function renderCountdown(el: HTMLElement, data: CountdownData): boolean {
    syncCountdownGroup(el, data.closed, data.urgent);
    if (data.closed) {
        el.classList.add('footer-mega__countdown-value--closed');
        el.classList.remove('footer-mega__countdown-value--urgent');
        el.textContent = 'Closed';
        return false;
    }
    el.classList.remove('footer-mega__countdown-value--closed');
    el.classList.toggle('footer-mega__countdown-value--urgent', data.urgent);
    if (el.textContent !== data.text) {
        el.textContent = data.text;
    }
    return true;
}

function tickCountdowns(els: HTMLElement[]): boolean {
    const nowMs = Date.now();
    let active = false;
    els.forEach((el) => {
        const endIso = el.dataset.end;
        if (!endIso) {
            return;
        }
        const endMs = Date.parse(endIso);
        if (Number.isNaN(endMs)) {
            return;
        }
        const secs = Math.floor((endMs - nowMs) / 1000);
        let data: CountdownData;
        if (el.dataset.countdownFormat === 'meeting') {
            if (secs <= 0) {
                data = { closed: true, text: 'Closed', urgent: false };
            } else {
                data = {
                    closed: false,
                    text: formatMeetingCountdownText(secs),
                    urgent: secs < 3600,
                };
            }
        } else {
            const secondsMode = (el.dataset.secondsMode as SecondsMode) || 'never';
            data = formatFooterCountdown(endMs, nowMs, secondsMode);
        }
        if (renderCountdown(el, data)) {
            active = true;
        }
    });
    return active;
}

function initFooterCountdowns(): void {
    const els = Array.from(
        document.querySelectorAll<HTMLElement>('.footer-mega__countdown-value[data-end]'),
    );
    if (!els.length) {
        return;
    }
    const run = (): boolean => {
        const active = tickCountdowns(els);
        if (!active && countdownInterval) {
            clearInterval(countdownInterval);
            countdownInterval = null;
        }
        return active;
    };
    const hasActive = run();
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }
    if (hasActive) {
        countdownInterval = setInterval(run, 1000);
    }
    if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
    }
    visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
            run();
        }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
}

export function footerMega() {
    document.querySelectorAll('.footer-mega__back-top').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const scrollToTop = (window as Window & { scrollToTop?: () => void }).scrollToTop;
            if (scrollToTop) {
                scrollToTop();
                return;
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    });
    initFooterCountdowns();
}
